import { createReducer, isAnyOf } from '@reduxjs/toolkit';
import { electron } from 'lib-shared-electron';
import intersectionBy from 'lodash/intersectionBy';
import omit from 'lodash/omit';
import moment from 'moment-timezone';
import { analyticsActions } from './analytics.actions';
import { isOptionalOperator } from './components/controls/utils/filters/isOptionalOperator';
export const initialState = {
    dashboard: undefined,
    context: {},
    period: { field: 'created_at', values: [{ value: 'today' }] },
    filters: [],
    settings: {
        timezone: moment.tz.guess(),
        locale: moment.locale(),
    },
    meta: {
        lastRefresh: new Date().toISOString(),
        openLinksInNewTab: 'SharedWorker' in window && !electron.isElectron(),
    },
};
export const analyticsReducer = createReducer(initialState, (builder) => builder
    .addCase(analyticsActions.setDashboard, (state, { payload: { dashboard } }) => {
    state.dashboard = dashboard;
    // Add the default context for the dashboard, if the dashboards supports context and a
    // previously selected context doesn't already exist in the state.
    if (dashboard.context && !state.context[dashboard.id]) {
        const values = dashboard.context.values.filter(({ preselected }) => preselected);
        state.context[dashboard.id] = {
            field: dashboard.context.field,
            values,
            operator: 'is_one',
        };
    }
    // Some dashboards might have some filters applied that aren't expected in other dashboards, so
    // we remove them when switching dashboards.
    state.filters.forEach((filter, i) => {
        const dashboardFilter = dashboard.filters.find(({ field }) => field === filter.field);
        // The next dashboard does not support the filter, removing it from the state.
        if (!dashboardFilter) {
            state.filters.splice(i, 1);
            return;
        }
        // The next dashboard supports the filter, but some selected values might be obsolete.
        if (filter.field !== 'custom_fields') {
            filter.values = dashboardFilter.values.length
                ? intersectionBy(dashboardFilter.values, filter.values, 'value')
                : filter.values;
        }
        // If no values remain selected, and the operator is not a optional operator, remove the filter.
        if (!filter.values.length && !isOptionalOperator(filter.operator)) {
            state.filters.splice(i, 1);
        }
    });
})
    .addCase(analyticsActions.setQuickView, (state, { payload: { dashboard, quickView } }) => {
    // Store a snapshot of the current state, which will be used to restore the state after
    // viewing a quick view and leaving Analytics. We want to always remember the last dashboard you
    // visited along with the filters applied, irrespective of which quick views you viewed after.
    if (!state.snapshot) {
        state.snapshot = omit(state, ['meta', 'snapshot']);
    }
    state.dashboard = dashboard;
    if (quickView.params.context) {
        state.context[dashboard.id] = quickView.params
            .context;
    }
    state.period = quickView.params.period;
    state.filters = quickView.params.filters;
    state.settings = omit(quickView.params.settings, 'granularity');
})
    .addCase(analyticsActions.setContext, (state, { payload: { context } }) => {
    if (state.dashboard) {
        state.context[state.dashboard.id] = Object.assign(Object.assign({}, context), { values: [...context.values] });
    }
})
    .addCase(analyticsActions.setPeriod, (state, { payload }) => {
    state.period = payload.period;
})
    .addCase(analyticsActions.setFilters, (state, { payload }) => {
    state.filters = payload.filters;
})
    .addCase(analyticsActions.setSettings, (state, { payload }) => {
    state.settings = Object.assign(Object.assign({}, state.settings), payload.settings);
})
    .addCase(analyticsActions.setMeta, (state, { payload }) => {
    state.meta = Object.assign(Object.assign({}, state.meta), payload.meta);
})
    // Restores the state to the previously saved snapshot, if any exists.
    .addCase(analyticsActions.restoreSnapshot, (state) => {
    if (state.snapshot) {
        for (const [key, value] of Object.entries(state.snapshot)) {
            state[key] = value;
        }
        delete state.snapshot;
    }
})
    // Updates the last refresh timestamp when the dashboard changes, or when any of the
    // user-configurable data is updated, such as the dashboard context, period or filters. Once the
    // timestamp is updated, the filters data and the instrument data in each panel is refreshed.
    .addMatcher(isAnyOf(analyticsActions.setDashboard, analyticsActions.setQuickView, analyticsActions.setContext, analyticsActions.setPeriod, analyticsActions.setFilters, analyticsActions.setSettings), (state) => {
    state.meta.lastRefresh = new Date().toISOString();
}));
